import React, { useMemo, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Link } from 'react-router-dom';
import { Download, ExternalLink, MapPin, MessageCircle, Printer, QrCode } from 'lucide-react';
import { useRestaurant } from '../../context/RestaurantContext';
import ArabAliLogo from '../../components/ArabAliLogo';

const SUPPORT_PHONE = '0907806267';
const TELEGRAM_HANDLE = '@muay011';
const TELEGRAM_URL = 'https://t.me/muay011';

const QRCodePage: React.FC = () => {
  const { restaurant, settings, mapsHref } = useRestaurant();
  const menuUrl = restaurant.menuUrl;
  const qrRef = useRef<HTMLDivElement>(null);

  const fileSafeName = useMemo(
    () => restaurant.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
    [restaurant.name],
  );

  const downloadSvg = () => {
    const svg = qrRef.current?.querySelector('svg');

    if (!svg) {
      return;
    }

    const serializer = new XMLSerializer();
    const source = serializer.serializeToString(svg);
    const blob = new Blob([source], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileSafeName}-qr.svg`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const downloadPng = async () => {
    const svg = qrRef.current?.querySelector('svg');

    if (!svg) {
      return;
    }

    const serializer = new XMLSerializer();
    const source = serializer.serializeToString(svg);
    const encoded = window.btoa(unescape(encodeURIComponent(source)));
    const image = new Image();
    const canvas = document.createElement('canvas');
    const size = 1200;
    canvas.width = size;
    canvas.height = size;
    const context = canvas.getContext('2d');

    if (!context) {
      return;
    }

    image.onload = () => {
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, size, size);
      context.drawImage(image, 0, 0, size, size);
      const link = document.createElement('a');
      link.href = canvas.toDataURL('image/png');
      link.download = `${fileSafeName}-qr.png`;
      link.click();
    };

    image.src = `data:image/svg+xml;base64,${encoded}`;
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen px-4 py-6 sm:px-5 sm:py-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <section className="overflow-hidden rounded-[32px] border border-white/10 bg-white/[0.04] shadow-[0_24px_60px_rgba(0,0,0,0.28)] backdrop-blur-sm">
          <div className="grid gap-0 lg:grid-cols-[1.05fr_0.95fr]">
            <div className="border-b border-white/10 p-6 sm:p-8 lg:border-b-0 lg:border-r lg:p-10">
              <ArabAliLogo
                logoImage={settings.logoImage}
                name={restaurant.name}
                subtitle={settings.subtitle || 'Restaurant'}
                location={restaurant.location}
                badgeLabel="Scan To Open"
              />

              <div className="mt-8 inline-flex items-center gap-2 rounded-full border border-amber-300/15 bg-amber-300/8 px-4 py-2 text-[11px] uppercase tracking-[0.28em] text-amber-100/90">
                <QrCode className="h-4 w-4" />
                Live QR Menu
              </div>

              <h1 className="mt-5 text-3xl font-semibold text-white sm:text-4xl">
                Download, print, and share the guest menu QR code.
              </h1>
              <p className="mt-4 max-w-2xl text-sm leading-7 text-white/65 sm:text-base">
                This QR code opens the live menu on your custom domain. Guests can scan it from tables, posters, or cards,
                and the printed materials stay useful because the menu updates after every publish.
              </p>

              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                <a
                  href={mapsHref}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-white/10 bg-white/6 px-5 py-3 text-sm font-medium text-white/85 transition hover:bg-white/10"
                >
                  <MapPin className="h-4 w-4 text-amber-300" />
                  Open location
                  <ExternalLink className="h-4 w-4 text-white/50" />
                </a>
                <a
                  href={TELEGRAM_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-sky-300/15 bg-sky-400/10 px-5 py-3 text-sm font-medium text-sky-100 transition hover:bg-sky-400/16"
                >
                  <MessageCircle className="h-4 w-4" />
                  Telegram {TELEGRAM_HANDLE}
                </a>
              </div>

              <div className="mt-6 rounded-[28px] border border-white/10 bg-black/15 p-5">
                <div className="text-[11px] uppercase tracking-[0.28em] text-white/45">Menu Link</div>
                <div className="mt-3 break-all text-sm leading-7 text-amber-100/90">{menuUrl}</div>
                <div className="mt-4 text-xs uppercase tracking-[0.24em] text-white/40">Support phone</div>
                <div className="mt-2 text-sm text-white/70">{SUPPORT_PHONE}</div>
              </div>
            </div>

            <div className="p-6 sm:p-8 lg:p-10">
              <div className="mx-auto max-w-md rounded-[32px] border border-amber-200/15 bg-[linear-gradient(180deg,rgba(255,255,255,0.92),rgba(246,229,193,0.92))] p-5 text-[#2d1309] shadow-[0_20px_60px_rgba(0,0,0,0.28)] sm:p-6">
                <div className="rounded-[28px] bg-white p-5 shadow-[inset_0_0_0_1px_rgba(75,33,12,0.08)] sm:p-6">
                  <div ref={qrRef} className="flex justify-center rounded-[24px] bg-white p-4 sm:p-5">
                    <QRCodeSVG value={menuUrl} size={260} level="H" includeMargin />
                  </div>

                  <div className="mt-5 text-center">
                    <div className="text-[11px] uppercase tracking-[0.32em] text-[#8b5d32]">Scan Here</div>
                    <div className="mt-3 text-2xl font-semibold">{restaurant.name}</div>
                    <div className="mt-2 text-sm text-[#714d33]">Open the live digital menu on your phone</div>
                    <div className="mt-4 inline-flex rounded-full border border-[#cfa66f] bg-[#f8ecd1] px-4 py-2 text-xs font-semibold tracking-[0.18em] text-[#82582e]">
                      {settings.callToActionLabel}
                    </div>
                  </div>

                  <div className="mt-5 rounded-[22px] bg-[#f7ecd8] px-4 py-4 text-center">
                    <div className="text-xs uppercase tracking-[0.24em] text-[#93683d]">Need help?</div>
                    <div className="mt-2 text-sm font-medium text-[#442213]">{SUPPORT_PHONE}</div>
                    <div className="mt-1 text-sm text-[#6d4426]">{TELEGRAM_HANDLE}</div>
                  </div>
                </div>

                <div className="mt-5 grid gap-3 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={downloadPng}
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-[#30120b] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#1f0c07]"
                  >
                    <Download className="h-4 w-4" />
                    Download PNG
                  </button>
                  <button
                    type="button"
                    onClick={downloadSvg}
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-[#30120b]/15 bg-white px-5 py-3 text-sm font-semibold text-[#30120b] transition hover:bg-[#fff7ea]"
                  >
                    <Download className="h-4 w-4" />
                    Download SVG
                  </button>
                  <button
                    type="button"
                    onClick={handlePrint}
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-[#30120b]/10 bg-[#f6e7c1] px-5 py-3 text-sm font-semibold text-[#30120b] transition hover:bg-[#f2ddb0] sm:col-span-2"
                  >
                    <Printer className="h-4 w-4" />
                    Print Ready Cards
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="grid gap-4 lg:grid-cols-3 print:hidden">
          {[
            {
              title: 'Table cards',
              body: 'Print small folded cards and place one on every dining table.',
            },
            {
              title: 'Reception poster',
              body: 'Use a larger print near the entrance so guests can scan before sitting down.',
            },
            {
              title: 'Social contact',
              body: 'Keep the Telegram handle visible for guests who need quick help or directions.',
            },
          ].map((item) => (
            <div key={item.title} className="rounded-[28px] border border-white/10 bg-white/[0.04] p-5">
              <div className="text-lg font-semibold text-white">{item.title}</div>
              <div className="mt-2 text-sm leading-7 text-white/60">{item.body}</div>
            </div>
          ))}
        </section>

        <section className="hidden print:block">
          <div className="print-page">
            <div className="grid grid-cols-2 gap-8">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={`card-${index}`} className="rounded-[28px] border-2 border-[#d7b073] bg-white p-8 text-center">
                  <div className="mb-5 flex justify-center">
                    <QRCodeSVG value={menuUrl} size={160} level="H" includeMargin />
                  </div>
                  <div className="text-2xl font-bold text-[#2d1309]">{restaurant.name}</div>
                  <div className="mt-2 text-sm text-[#6f4b31]">Scan to open the live digital menu</div>
                  <div className="mt-4 text-xs uppercase tracking-[0.24em] text-[#9b6b41]">{restaurant.location}</div>
                  <div className="mt-4 text-sm text-[#3d1a0f]">{SUPPORT_PHONE}</div>
                  <div className="mt-1 text-sm text-[#6d4426]">{TELEGRAM_HANDLE}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="page-break" />

          <div className="print-page">
            <div className="rounded-[36px] border-4 border-[#d7b073] bg-white p-12 text-center">
              <div className="text-[12px] uppercase tracking-[0.35em] text-[#9b6b41]">Scan To View Menu</div>
              <h1 className="mt-4 text-5xl font-bold text-[#2d1309]">{restaurant.name}</h1>
              <div className="mt-3 text-lg text-[#6f4b31]">{settings.subtitle || 'Restaurant Menu'}</div>
              <div className="mt-10 flex justify-center">
                <QRCodeSVG value={menuUrl} size={320} level="H" includeMargin />
              </div>
              <div className="mt-8 text-xl font-medium text-[#3d1a0f]">Point your camera at the QR code</div>
              <div className="mt-4 text-base text-[#6f4b31]">{restaurant.location}</div>
              <div className="mt-6 text-sm text-[#3d1a0f]">{SUPPORT_PHONE}</div>
              <div className="mt-2 text-sm text-[#6d4426]}">{TELEGRAM_HANDLE}</div>
            </div>
          </div>
        </section>

        <div className="text-center print:hidden">
          <Link
            to="/menu"
            className="inline-flex items-center justify-center rounded-full border border-white/10 bg-white/5 px-7 py-3 text-sm font-semibold text-white transition hover:bg-white/10"
          >
            Back to Menu
          </Link>
        </div>
      </div>

      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 1cm;
          }

          body {
            background: white !important;
          }

          .print-page {
            min-height: 100vh;
            padding: 0.5cm 0;
          }

          .page-break {
            page-break-after: always;
          }
        }
      `}</style>
    </div>
  );
};

export default QRCodePage;
